import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Baby, Search, Loader2, Pencil, Eye, Download, BookOpen, BarChart3, Calendar, ArrowLeftRight, Filter } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import AddClassDialog from "@/components/dialogs/AddClassDialog";
import EditClassDialog from "@/components/dialogs/EditClassDialog";
import DeleteConfirmDialog from "@/components/dialogs/DeleteConfirmDialog";
import ClassStatsCards from "@/components/classes/ClassStatsCards";
import ClassDetailDialog from "@/components/classes/ClassDetailDialog";
import TransferStudentDialog from "@/components/classes/TransferStudentDialog";
import ClassScheduleView from "@/components/classes/ClassScheduleView";
import ClassAnalytics from "@/components/classes/ClassAnalytics";

const Classes = () => {
  const { profile, roles, user } = useAuth();
  const { toast } = useToast();
  const nurseryId = profile?.nursery_id;
  const [search, setSearch] = useState("");
  const [editClass, setEditClass] = useState<any>(null);
  const [detailClass, setDetailClass] = useState<any>(null);
  const [filterTeacher, setFilterTeacher] = useState<string>("all");

  const { data: classes, isLoading, refetch } = useQuery({
    queryKey: ["classes", nurseryId],
    queryFn: async () => {
      if (!nurseryId) return [];
      const { data, error } = await supabase
        .from("classes")
        .select("*, staff(full_name)")
        .eq("nursery_id", nurseryId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const { data: childrenCounts } = useQuery({
    queryKey: ["children-per-class", nurseryId],
    queryFn: async () => {
      if (!nurseryId) return {};
      const { data } = await supabase.from("children").select("class_id").eq("nursery_id", nurseryId);
      const counts: Record<string, number> = {};
      (data || []).forEach((c) => { if (c.class_id) counts[c.class_id] = (counts[c.class_id] || 0) + 1; });
      return counts;
    },
    enabled: !!nurseryId,
  });

  const { data: staffList } = useQuery({
    queryKey: ["staff-list", nurseryId],
    queryFn: async () => {
      if (!nurseryId) return [];
      const { data } = await supabase.from("staff").select("id, full_name, user_id").eq("nursery_id", nurseryId);
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const counts = childrenCounts || {};
  const allClasses = classes || [];
  const allStaff = staffList || [];
  const currentStaffId = allStaff.find((member: any) => member.user_id === user?.id)?.id;
  const isTeacherOnly = roles.includes("teacher") && !roles.includes("admin") && !roles.includes("supervisor");
  const scheduleClasses = isTeacherOnly
    ? allClasses.filter((classItem) => classItem.teacher_id === currentStaffId)
    : allClasses;

  const filtered = allClasses
    .filter((c) => c.name.includes(search))
    .filter((c) => filterTeacher === "all" || c.teacher_id === filterTeacher);

  // Stats
  const totalStudents = Object.values(counts).reduce((a, b) => a + b, 0);
  const totalCapacity = allClasses.reduce((a, c) => a + (c.capacity || 25), 0);
  const avgOccupancy = totalCapacity > 0 ? Math.round((totalStudents / totalCapacity) * 100) : 0;

  const handleExport = () => {
    const rows = [["الفصل", "الفئة العمرية", "المعلمة", "الطلاب", "السعة", "الإشغال %"]];
    allClasses.forEach((cls) => {
      const sc = counts[cls.id] || 0;
      const cap = cls.capacity || 25;
      rows.push([cls.name, cls.age_group || "-", (cls as any).staff?.full_name || "-", String(sc), String(cap), String(Math.round((sc / cap) * 100))]);
    });
    const csv = "\uFEFF" + rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "classes.csv"; a.click();
    URL.revokeObjectURL(url);
    toast({ title: "تم تصدير البيانات ✅" });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-primary" />
            إدارة الفصول
          </h1>
          <p className="text-muted-foreground text-sm mt-1">تنظيم الفصول والجداول الدراسية وتوزيع الطلاب</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <TransferStudentDialog classes={allClasses} onSuccess={refetch} />
          <Button variant="outline" onClick={handleExport} className="gap-2">
            <Download className="w-4 h-4" /> تصدير
          </Button>
          <AddClassDialog staff={staffList || []} onSuccess={refetch} />
        </div>
      </div>

      {/* Stats */}
      <ClassStatsCards
        totalClasses={allClasses.length}
        totalStudents={totalStudents}
        totalCapacity={totalCapacity}
        avgOccupancy={avgOccupancy}
      />

      {/* Tabs */}
      <Tabs defaultValue="classes" dir="rtl">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="classes" className="gap-1.5"><BookOpen className="w-4 h-4" /> الفصول</TabsTrigger>
          <TabsTrigger value="schedule" className="gap-1.5"><Calendar className="w-4 h-4" /> الجدول الزمني</TabsTrigger>
          <TabsTrigger value="analytics" className="gap-1.5"><BarChart3 className="w-4 h-4" /> التحليلات</TabsTrigger>
        </TabsList>

        {/* Classes Tab */}
        <TabsContent value="classes" className="space-y-4 mt-4">
          <div className="flex gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="بحث عن فصل..." value={search} onChange={(e) => setSearch(e.target.value)} className="pr-9" />
            </div>
            <Select value={filterTeacher} onValueChange={setFilterTeacher}>
              <SelectTrigger className="w-48">
                <Filter className="w-4 h-4 ml-2 text-muted-foreground" />
                <SelectValue placeholder="فلتر حسب المعلمة" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع المعلمات</SelectItem>
                {(staffList || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-muted-foreground">لا توجد فصول</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((cls, i) => {
                const studentCount = counts[cls.id] || 0;
                const capacity = cls.capacity || 25;
                const percentage = Math.min((studentCount / capacity) * 100, 100);
                const isHigh = percentage > 80;
                return (
                  <motion.div key={cls.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                    <Card className="shadow-card hover:shadow-elevated transition-all group cursor-pointer" onClick={() => setDetailClass(cls)}>
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-bold text-card-foreground text-lg">{cls.name}</h3>
                          <span className="text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-full">{cls.age_group || "-"}</span>
                        </div>
                        <div className="space-y-3 text-sm">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Users className="w-4 h-4" />
                            <span>المعلمة: {(cls as any).staff?.full_name || "غير محدد"}</span>
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Baby className="w-4 h-4" />
                            <span>الطلبة: <span className={isHigh ? "text-destructive font-medium" : ""}>{studentCount}</span>/{capacity}</span>
                          </div>
                          <div className="h-2 rounded-full bg-muted overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${isHigh ? "bg-destructive" : "gradient-hero"}`} style={{ width: `${percentage}%` }} />
                          </div>
                        </div>
                        <div className="flex gap-2 mt-4 pt-3 border-t border-border" onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="gap-1 text-primary" onClick={() => setDetailClass(cls)}>
                            <Eye className="w-3.5 h-3.5" /> التفاصيل
                          </Button>
                          <Button variant="outline" size="sm" className="gap-1" onClick={() => setEditClass(cls)}>
                            <Pencil className="w-3.5 h-3.5" /> تعديل
                          </Button>
                          <DeleteConfirmDialog
                            title="حذف الفصل"
                            description="هل أنت متأكد من حذف هذا الفصل؟ لا يمكن التراجع."
                            onConfirm={async () => {
                              const { error } = await supabase.from("classes").delete().eq("id", cls.id);
                              if (error) toast({ title: "خطأ", description: error.message, variant: "destructive" });
                              else { toast({ title: "تم حذف الفصل" }); refetch(); }
                            }}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Schedule Tab */}
        <TabsContent value="schedule" className="mt-4">
          <ClassScheduleView
            classes={scheduleClasses}
            staff={allStaff}
            emptyStateMessage={isTeacherOnly ? "لا يوجد فصل مُسند إلى حسابك حالياً. اطلب من إدارة الحضانة ربطك بالفصل أولاً." : undefined}
          />
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="mt-4">
          <ClassAnalytics classes={allClasses} childrenCounts={counts} />
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <EditClassDialog open={!!editClass} onOpenChange={(o) => !o && setEditClass(null)} classData={editClass} staff={staffList || []} onSuccess={refetch} />
      <ClassDetailDialog open={!!detailClass} onOpenChange={(o) => !o && setDetailClass(null)} classData={detailClass} />
    </div>
  );
};

export default Classes;
